import { extractErrorMessage } from "./error-handling";
import { getLoadedPostHog } from "./posthog-client";

/**
 * HTTP status code to user-friendly message mapping
 */
const ERROR_MESSAGES: Record<number, string> = {
  400: "The request was invalid. Please check your input and try again.",
  401: "You need to sign in to continue.",
  403: "You don't have permission to access this resource.",
  404: "The requested item could not be found.",
  429: "You're doing that too fast. Please wait a moment and try again.",
  500: "Something went wrong on our end. Please try again later.",
};

const GENERIC_ERROR = "Something went wrong. Please try again.";

/**
 * Extracts error code from various error types
 */
function extractErrorCode(error: unknown): number | null {
  if (error && typeof error === "object") {
    const err = error as Record<string, unknown>;

    // Check for code property (ServerError format)
    if (typeof err.code === "number") {
      return err.code;
    }

    // Check for status property (fetch responses)
    if (typeof err.status === "number") {
      return err.status;
    }

    // Check for statusCode property
    if (typeof err.statusCode === "number") {
      return err.statusCode;
    }

    // Try to parse error message as JSON for ServerError format
    if (error instanceof Error) {
      try {
        const parsed = JSON.parse(error.message);
        if (typeof parsed.code === "number") {
          return parsed.code;
        }
      } catch {
        // Not JSON, continue
      }
    }
  }

  return null;
}

/**
 * Returns a user-friendly error message.
 * In development, shows detailed error messages.
 * In production, shows generic messages based on error code.
 *
 * @param error The error object from a catch block
 * @returns A user-friendly error message
 */
export function getUserFriendlyMessage(error: unknown): string {
  // In development, show detailed error for debugging
  if (process.env.NODE_ENV === "development") {
    return extractErrorMessage(error);
  }

  // In production, show generic message based on error code
  const code = extractErrorCode(error);
  if (code && ERROR_MESSAGES[code]) {
    return ERROR_MESSAGES[code];
  }

  return GENERIC_ERROR;
}

/**
 * Captures an error to PostHog for debugging.
 * Safe to call in any environment - will only send if PostHog is initialized
 * and the user has granted analytics consent.
 *
 * @param error The error to capture
 * @param context Additional context to attach to the error
 */
export function captureError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  const posthog = typeof window !== "undefined" ? getLoadedPostHog() : null;

  if (posthog?.__loaded && !posthog.has_opted_out_capturing()) {
    posthog.captureException(error, {
      ...context,
      original_message: error instanceof Error ? error.message : String(error),
    });
  }
}
